/**
 * **主题 CSS 的作用域变换** —— 通道 ③（逃生舱）的地基。
 *
 * ## 为什么需要它
 *
 * 主题包可以带任意 CSS（这是「基本不受限制」那一半）。但
 * `@node-print/designer` 是**要被集成进别人的 Vue 项目**的（见 `index.ts`），
 * 一段没有作用域的 CSS 能直接把宿主页面掀掉 —— 作者手滑写一句
 * 「body 不显示」，宿主整站就白屏了。
 * 所以注入前统一变换：把每条规则挂到 `html[data-np-theme="<id>"]` 之下。
 *
 * ## 为什么不用 `@scope` / Shadow DOM
 *
 * * `@scope` —— 支持面比 `:has()` 还窄。而这个仓库**已经被 `:has()` 坑过**：
 *   用户的手机上「一半翻了一半没翻」，查了半天（`verify:css` 里那条
 *   「浅色主题不依赖 `:has()`」的断言就是那次留下的）。同样的坑不踩第二次。
 * * Shadow DOM —— Teleport 到 `body` 的菜单、Vue Flow 的 DOM 全都拿不到样式。
 *
 * 所以用最笨也最稳的办法：**前缀选择器**。它的正确性靠夹具表保证
 * （`verify:theme`），不靠「我觉得应该对」。
 *
 * ## ⚠️ 这个变换最危险的失败模式是「静默失效」
 *
 * 一旦变换写错，主题 CSS 会**整块不生效而且不报错** —— 看起来像
 * 「作者没写」。这个仓库为这一类错记过好几条（见 `verify-css.ts` 头部），
 * 所以 `verify:theme` 里钉了一张**夹具表**，覆盖嵌套 at-rule / 注释里
 * 带大括号 / 字符串里带大括号 / 逗号选择器 / `@keyframes` / `@global`。
 */

/**
 * 这些 at-rule 的**内容不是选择器**，一律整块透传。
 *
 * ⚠️ 漏一个的后果：`@keyframes spin { from {…} }` 里的 `from` 会被
 * 当成选择器加上前缀 → 关键帧**永远不触发**，而样式表看起来完全正常。
 */
const PASSTHROUGH_AT = new Set([
  'keyframes',
  '-webkit-keyframes',
  '-moz-keyframes',
  'font-face',
  'font-feature-values',
  'font-palette-values',
  'property',
  'page',
  'counter-style',
  'charset',
  'namespace',
  'import',
  'layer', // `@layer a, b;` 这种只有名字没有块的；有块的走 NESTED_AT
  'viewport',
  'color-profile',
])

/**
 * 条件组规则：**里面还是规则**，所以要递归变换，但 prelude 保持原样。
 *
 * ⚠️ `layer` 同时出现在两个表里是**故意的**：
 * `@layer a, b;`（无块，透传）与 `@layer x { … }`（有块，递归）都要对。
 * 靠「有没有块」来分流，见 `transform()`。
 */
const NESTED_AT = new Set(['media', 'supports', 'layer', 'container', 'document'])

/** 作者用来**显式**放弃作用域的标记 —— 后果自负，所以必须写全拼 */
const GLOBAL_AT = 'global'

/** 模板里要替换掉的前缀选择器（它们指向的本来就是根） */
const ROOT_PREFIX = /^(:root|:host|html)(?![\w-])/i

interface Block {
  /** 大括号之前那一段（选择器，或 `@media …` 这样的 at-prelude） */
  prelude: string
  /** 大括号里的内容。`undefined` = 这是一条以 `;` 结束的语句（`@import` 等） */
  body?: string
  /**
   * 选择器**前面**的整段注释。
   *
   * ⚠️ 必须和选择器分开存。合在一起的话，加前缀会把注释插到
   * **选择器中间**，于是这条规则永远匹配不到，而生成的 CSS
   * 看起来"挺正常"。夹具表里有一条专门盯它。
   */
  leading?: string
}

/** 把开头的整段注释和剩下的选择器分开 */
function splitLeadingComments(text: string): { comments: string; rest: string } {
  let rest = text.trimStart()
  const comments: string[] = []
  for (;;) {
    if (!rest.startsWith('/*')) break
    const end = rest.indexOf('*/')
    if (end === -1) break
    comments.push(rest.slice(0, end + 2))
    rest = rest.slice(end + 2).trimStart()
  }
  /*
   * ⚠️ 结尾也要 trim。第一版只做了 `trimStart`，于是选择器带着尾随空格
   * 进入 `prelude`，输出变成 `@keyframes spin  {`（两个空格）——
   * 夹具表当场抓到了它。CSS 不在乎多一个空格，但
   * 「生成出来的东西和预期逐字符不一致」会让夹具失去意义：
   * 一旦容许空白差异，真正的结构错误也会被宽容掉。
   */
  return { comments: comments.join('\n'), rest: rest.trim() }
}

/**
 * 把一份样式表切成**顶层**语句/块。
 *
 * ⚠️ 必须同时跳过注释与字符串：
 *   · 注释里出现 `{` 是常事（本项目自己的注释里就一堆），
 *     按字符数大括号会彻底错位；
 *   · `content: "}"` 同理。
 * 这两条都在夹具表里钉着。
 */
function splitTopLevel(css: string): Block[] {
  const blocks: Block[] = []
  let buf = ''
  let i = 0

  const flushStatement = () => {
    const { comments, rest } = splitLeadingComments(buf)
    if (rest) blocks.push({ prelude: rest, leading: comments || undefined })
    buf = ''
  }

  while (i < css.length) {
    const ch = css[i]!

    /* ── 注释：整段原样收进 buf ── */
    if (ch === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2)
      const stop = end === -1 ? css.length : end + 2
      buf += css.slice(i, stop)
      i = stop
      continue
    }

    /* ── 字符串：整段原样收进 buf（避免 `content: "{"` 扰乱计数）── */
    if (ch === '"' || ch === "'") {
      let j = i + 1
      while (j < css.length) {
        if (css[j] === '\\') {
          j += 2
          continue
        }
        if (css[j] === ch) break
        j += 1
      }
      buf += css.slice(i, Math.min(j + 1, css.length))
      i = j + 1
      continue
    }

    if (ch === '{') {
      /* 找到配对的 `}` —— 同样要跳注释与字符串 */
      const body = readBlockBody(css, i + 1)
      const { comments, rest } = splitLeadingComments(buf)
      blocks.push({ prelude: rest, body: body.text, leading: comments || undefined })
      buf = ''
      i = body.next
      continue
    }

    if (ch === ';') {
      flushStatement()
      i += 1
      continue
    }

    buf += ch
    i += 1
  }

  flushStatement()
  return blocks
}

/** 从 `{` 之后读到配对 `}`，返回内容与下一个读取位置 */
function readBlockBody(css: string, start: number): { text: string; next: number } {
  let depth = 1
  let i = start
  let out = ''
  while (i < css.length) {
    const ch = css[i]!
    if (ch === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2)
      const stop = end === -1 ? css.length : end + 2
      out += css.slice(i, stop)
      i = stop
      continue
    }
    if (ch === '"' || ch === "'") {
      let j = i + 1
      while (j < css.length) {
        if (css[j] === '\\') {
          j += 2
          continue
        }
        if (css[j] === ch) break
        j += 1
      }
      out += css.slice(i, Math.min(j + 1, css.length))
      i = j + 1
      continue
    }
    if (ch === '{') depth += 1
    else if (ch === '}') {
      depth -= 1
      if (depth === 0) return { text: out, next: i + 1 }
    }
    out += ch
    i += 1
  }
  /* 没闭合 —— 原样收下，不再往下吃（宁可少变换也不要吞掉后半张表） */
  return { text: out, next: css.length }
}

/** 按**顶层**逗号切选择器列表（`:is(a, b)` 里的逗号不能切） */
function splitSelectors(list: string): string[] {
  const out: string[] = []
  let depth = 0
  let buf = ''
  let i = 0
  while (i < list.length) {
    const ch = list[i]!
    if (ch === '(' || ch === '[') depth += 1
    else if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1)
    if (ch === ',' && depth === 0) {
      out.push(buf.trim())
      buf = ''
      i += 1
      continue
    }
    buf += ch
    i += 1
  }
  if (buf.trim()) out.push(buf.trim())
  return out.filter(Boolean)
}

/** 给一条选择器加作用域前缀 */
function scopeSelector(selector: string, scope: string): string {
  const s = selector.trim()
  if (!s) return s
  /* 已经带了作用域就别重复加（作者可能从 devtools 里抄回来的） */
  if (s.startsWith(scope)) return s
  /*
   * `:root` / `html` / `:host` 指向的本来就是文档根，
   * 直接**替换**成作用域选择器。
   *
   * ⚠️ 不能写成前缀（`scope :root`）—— 那个选择器**永远匹配不到任何元素**，
   * 而 CSS 不会为此报任何错。这是本文件最需要夹具表盯住的一条。
   */
  if (ROOT_PREFIX.test(s)) return s.replace(ROOT_PREFIX, scope)
  return `${scope} ${s}`
}

/** `@media (…)` → `media`；`@-webkit-keyframes` → `-webkit-keyframes` */
function atName(prelude: string): string {
  const m = /^@([\w-]+)/.exec(prelude.trim())
  return (m?.[1] ?? '').toLowerCase()
}

function transform(blocks: Block[], scope: string, indent: string): string {
  const out: string[] = []
  for (const block of blocks) {
    const prelude = block.prelude

    /* ── 没有块的语句（`@import …;`）── */
    if (block.body === undefined) {
      if (block.leading) out.push(`${indent}${block.leading}`)
      out.push(`${indent}${prelude};`)
      continue
    }

    /* ── at-rule ── */
    if (prelude.startsWith('@')) {
      const name = atName(prelude)

      if (name === GLOBAL_AT) {
        /*
         * 作者显式要求「这段别加作用域」。
         *
         * ⚠️ 这是**放弃保护**，不是 bug —— 所以输出里留一行标记，
         * 在 devtools 里一眼能看出这段是作者自己担责的。
         *
         * 两种写法都收：
         *   `@global { .a { … } }`  整块不加作用域
         *   `@global .a { … }`      只这一条不加作用域
         * 后者如果不特判，`@global .a` 会被当成一个 at-rule 名，
         * 于是选择器 `.a` 连同它的声明**整条丢掉** —— 静默失效。
         */
        const rest = prelude.slice(GLOBAL_AT.length + 1).trim()
        out.push(`${indent}/* @global —— 按作者要求未加作用域 */`)
        out.push(rest ? `${indent}${rest} {${block.body}}` : block.body.trim())
        continue
      }

      if (PASSTHROUGH_AT.has(name) && !NESTED_AT.has(name)) {
        out.push(`${indent}${prelude} {${block.body}}`)
        continue
      }

      /* 条件组规则 / `@layer x { }`：递归 */
      const inner = transform(splitTopLevel(block.body), scope, `${indent}  `)
      out.push(`${indent}${prelude} {\n${inner}\n${indent}}`)
      continue
    }

    /* ── 普通规则 ── */
    const lead = block.leading ? `${indent}${block.leading}\n` : ''
    const scoped = splitSelectors(prelude)
      .map((s) => scopeSelector(s, scope))
      .join(`,\n${indent}`)
    out.push(`${lead}${indent}${scoped} {${block.body}}`)
  }
  return out.join('\n')
}

/**
 * 把主题 CSS 变换到指定作用域之下。
 *
 * @param css   作者写的原始 CSS
 * @param scope 作用域选择器，形如 `html[data-np-theme="midnight"]`
 * @returns 变换后的 CSS（可以直接塞进 `<style>`）
 *
 * @example
 * ```ts
 * scopeCss('.np-node { border-radius: 12px }', 'html[data-np-theme="x"]')
 * // → 'html[data-np-theme="x"] .np-node { border-radius: 12px }'
 * ```
 */
export function scopeCss(css: string, scope: string): string {
  if (!css.trim()) return ''
  return transform(splitTopLevel(css), scope, '').trim()
}

/**
 * 主题作用域的 CSS 选择器。
 *
 * ⚠️ 挂在 `<html>` 上而不是设计器根节点，理由是**继承与 Teleport**：
 * `.np-menu` / `.np-color-popover` 会 Teleport 到 `body` 之下
 * （见 `theme.css` 头部那段说明），挂在设计器根节点上的话
 * 这些弹层**一个令牌都继承不到**。
 *
 * 反过来，`html[data-np-theme=…] body .np-menu` 是能匹配到的 ——
 * `body` 本来就是 `html` 的后代。这也是 `scopeSelector` 不必对
 * `body` 做特判的原因。
 */
export function themeScopeSelector(themeId: string): string {
  return `html[data-np-theme="${cssEscapeAttr(themeId)}"]`
}

/** 属性选择器里的值转义（主题 id 里出现引号的话，别把选择器拼坏） */
function cssEscapeAttr(value: string): string {
  return value.replace(/["\\]/g, '\\$&')
}
